import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faCheckCircle,
  faCircle,
  faClock,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

interface ModuleProgress {
  moduleId: string;
  title: string;
  description: string;
  orderIndex: number;
  status: number; // 0 NotStarted, 1 InProgress, 2 Completed
  progressPercent: number;
  startedDate: string | null;
  completedDate: string | null;
}

interface EnrollmentDetail {
  enrollmentId: string;
  learnershipId: string;
  title: string;
  saqaId: string;
  summary: string;
  nqfLevel: number | null;
  credits: number | null;
  durationMonths: number;
  enrollmentDate: string;
  completionDate: string | null;
  status: number;
  overallProgressPercent: number;
  modules: ModuleProgress[];
}

const moduleStatusConfig: { [key: number]: { icon: any; className: string; label: string } } = {
  0: { icon: faCircle, className: 'text-brand-900/30', label: 'Not started' },
  1: { icon: faClock, className: 'text-yellow-600', label: 'In progress' },
  2: { icon: faCheckCircle, className: 'text-green-600', label: 'Completed' },
};

export default function ProgressPage() {
  const [enrollments, setEnrollments] = useState<EnrollmentDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useAuth();

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch('/api/learnerships/me/progress', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error('Failed to load progress');

      const data = await response.json();
      setEnrollments(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load your progress. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const overallAverage =
    enrollments.length > 0
      ? Math.round(
          enrollments.reduce((sum, e) => sum + e.overallProgressPercent, 0) / enrollments.length
        )
      : 0;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Progress</h1>
        <p className="text-sm text-brand-900/60 mt-1">
          Detailed module-by-module breakdown across all your learnerships
        </p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && enrollments.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faChartLine} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No progress to show yet</h3>
            <p className="text-sm text-brand-900/60 mt-2">
              Once you're enrolled in a learnership, your module progress will appear here.
            </p>
          </div>
        )}

        {!isLoading && !error && enrollments.length > 0 && (
          <>
            {/* Overall summary card */}
            <div className="card-surface p-6 mb-8">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-brand-900/60">
                  Overall progress across {enrollments.length} learnership
                  {enrollments.length > 1 ? 's' : ''}
                </h2>
                <span className="text-2xl font-bold text-brand-700">{overallAverage}%</span>
              </div>
              <div className="w-full bg-canvas-200 rounded-full h-3">
                <div
                  className="bg-brand-gradient h-3 rounded-full transition-all"
                  style={{ width: `${overallAverage}%` }}
                />
              </div>
            </div>

            {/* Per-learnership breakdown */}
            <div className="space-y-6">
              {enrollments.map((enrollment) => (
                <div key={enrollment.enrollmentId} className="card-surface p-6">
                  <div className="flex items-start justify-between mb-5 gap-4">
                    <div>
                      <h3 className="font-bold text-brand-950 text-lg">{enrollment.title}</h3>
                      <p className="text-xs text-brand-900/50 mt-1">SAQA {enrollment.saqaId}</p>
                    </div>
                    <span className="text-lg font-bold text-brand-700 whitespace-nowrap">
                      {enrollment.overallProgressPercent}%
                    </span>
                  </div>

                  {/* Module list */}
                  <div className="space-y-3">
                    {enrollment.modules.map((module) => {
                      const config = moduleStatusConfig[module.status] ?? moduleStatusConfig[0];
                      return (
                        <div
                          key={module.moduleId}
                          className="flex items-center gap-4 border border-canvas-200 rounded-xl p-3"
                        >
                          <FontAwesomeIcon
                            icon={config.icon}
                            className={`h-4 w-4 flex-shrink-0 ${config.className}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-brand-950 truncate">
                              {module.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex-1 bg-canvas-200 rounded-full h-1.5">
                                <div
                                  className="bg-brand-gradient h-1.5 rounded-full transition-all"
                                  style={{ width: `${module.progressPercent}%` }}
                                />
                              </div>
                              <span className="text-xs text-brand-900/50 whitespace-nowrap">
                                {module.progressPercent}%
                              </span>
                            </div>
                          </div>
                          <span className="text-xs font-medium text-brand-900/50 whitespace-nowrap hidden sm:inline">
                            {config.label}
                          </span>
                        </div>
                      );
                    })}

                    {enrollment.modules.length === 0 && (
                      <p className="text-sm text-brand-900/50 text-center py-4">
                        No modules configured for this learnership yet.
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
